import { Fragment, useContext } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import {
    BellIcon,
    BookOpenIcon, CogIcon, CreditCardIcon,
    CubeIcon,
    DocumentAddIcon, DocumentReportIcon, LogoutIcon,
    MenuAlt1Icon,
    PlusIcon, SupportIcon, UploadIcon,
    UserGroupIcon,
    UsersIcon, XIcon
} from "@heroicons/react/solid";
import AccessControl from "../SharedComponents/AccessControl";
import { BUTTON_VARIANT_TYPES, NO_ACCESS_TYPES, PERMISSIONS } from "../../utils/constants";
import Link from "./Menu/Link";
import CollapsibleLinks from "./Menu/CollapsibleLinks";
import Button from "../SharedComponents/Button";
import { DashboardContext } from "../../context/DashboardContext";
import { logout } from "../../utils/api";

function MobileMenuPopPanel() {
    const navigate = useNavigate();
    const context = useContext(DashboardContext);

    const handleSuccessfulLogout = () => {
        context.setLoggedIn(false);
        navigate("/login");
    }

    const handleLogout = async () => {
        try {
            await logout();
            handleSuccessfulLogout();
        } catch (alert) {
            console.log(alert.message);
        }
    }

    return (
        <Menu as="div" className="relative lg:hidden">

            {({ open }) => (
                <>
                    <div className={"relative flex items-center justify-between gap-2 cursor-pointer z-50"}>
                        <Menu.Button
                            className={
                                "bg-white rounded-md text-sm rounded-full hover:text-primary-700 " +
                                "hover:bg-primary-50 transition  " +
                                "transition-all duration-400  group flex items-center justify-center transform " +
                                (open
                                    ? "bg-blue-50 text-blue-700 shadow-lg shadow-blue-600/30 scale-105"
                                    : "from-bg-blue-200 text-primary-600 shadow-sm shadow-blue-600/30")
                            }
                        >
                            <span className="sr-only">Open user menu</span>
                            <div className={"relative"}>
                                <div className={"flex flex-row items-center gap-2 justify-between p-3 font-medium"}>
                                    {open ?
                                        <XIcon className={"h-6 w-6 cursor-pointer"} aria-hidden="true"/>
                                        :
                                        <MenuAlt1Icon className={"h-7 w-7 cursor-pointer"} aria-hidden="true"/>
                                    }
                                </div>
                            </div>
                        </Menu.Button>
                    </div>
                    <Transition
                        show={open}
                        as={Fragment}
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <Menu.Items
                            className={
                                "fixed z-40 left-0 top-0 h-screen overflow-y-scroll origin-top-left w-full " +
                                "focus:outline-none flex flex-col gap-2 z-30 bg-gradient-to-r " +
                                "bg-white "
                            }
                        >
                            <Menu.Item>
                                <div className={"grid grid-flow-row grid z-10 shadow-sm"}>
                                    <div className={"grid grid-flow-row content-start transition transition-height"}>
                                        <div className="py-8 pl-6 flex items-center text-indigo-900 w-40">
                                            <img alt="logo" src={context.getImage("logo")}/>
                                        </div>
                                        <div className={
                                            "py-5 px-6 flex items-center justify-between text-indigo-900 bg-gradient-to-r " +
                                            "from-secondary-50 to-primary-50"
                                        }>
                                            <div className={"flex flex-col"}>
                                                <span className="font-medium">
                                                    {context.userDetails.name}
                                                </span>
                                                <span className="font-light text-sm mt-1">
                                                    {context.userDetails.email}
                                                </span>
                                            </div>
                                        </div>
                                        <AccessControl
                                            requiredPermission={PERMISSIONS.ACCESS_APPLICATIONS}
                                            noAccessType={NO_ACCESS_TYPES.BLANK}
                                        >
                                            <Link
                                                name="Apps"
                                                href={"/apps"}
                                                icon={<CubeIcon className="block h-4 w-4 mr-2"/>}
                                            />
                                        </AccessControl>
                                        <AccessControl
                                            requiredPermission={PERMISSIONS.ACCESS_ASSESSMENTS}
                                            noAccessType={NO_ACCESS_TYPES.BLANK}
                                        >
                                            <CollapsibleLinks childLinkCount={1} show>
                                                <Link
                                                    href={"/assessments"}
                                                    name="Assessments"
                                                    icon={<BookOpenIcon className="block h-4 w-4 mr-2"/>}
                                                    parent
                                                />
                                                <AccessControl
                                                    requiredPermission={PERMISSIONS.ASSIGN_ASSESSMENTS}
                                                    noAccessType={NO_ACCESS_TYPES.BLANK}
                                                    href={"/assessments/assign-assessment"}
                                                >
                                                    <Link
                                                        name="Assign Assessment"
                                                        href={"/assessments/assign-assessment"}
                                                        icon={<DocumentAddIcon className="block h-4 w-4 mr-2"/>}
                                                        extraStyling={"ml-6"}
                                                    />
                                                </AccessControl>
                                            </CollapsibleLinks>
                                        </AccessControl>
                                        <AccessControl
                                            requiredPermission={PERMISSIONS.VIEW_USERS_LIST}
                                            noAccessType={NO_ACCESS_TYPES.BLANK}
                                        >
                                            <CollapsibleLinks childLinkCount={1} show>
                                                <Link
                                                    name="Users"
                                                    href={"/users"}
                                                    icon={<UsersIcon className="block h-4 w-4 mr-2"/>}
                                                    parent
                                                />
                                                <AccessControl
                                                    requiredPermission={PERMISSIONS.CREATE_USER_ACCOUNTS}
                                                    noAccessType={NO_ACCESS_TYPES.BLANK}
                                                    href={"/users/add-user"}
                                                >
                                                    <Link
                                                        name="Add User"
                                                        href={"/users/add-user"}
                                                        icon={<PlusIcon className="block h-4 w-4 mr-2"/>}
                                                        extraStyling={"ml-6"}
                                                    />
                                                </AccessControl>
                                            </CollapsibleLinks>
                                        </AccessControl>
                                        <AccessControl
                                            requiredPermission={PERMISSIONS.VIEW_USER_GROUPS_LIST}
                                            noAccessType={NO_ACCESS_TYPES.BLANK}
                                        >
                                            <CollapsibleLinks childLinkCount={1} show>
                                                <Link
                                                    name="User Groups"
                                                    href={"/user-groups"}
                                                    icon={<UserGroupIcon className="block h-4 w-4 mr-2"/>}
                                                    parent
                                                />
                                                <AccessControl
                                                    requiredPermission={PERMISSIONS.CREATE_USER_GROUPS}
                                                    noAccessType={NO_ACCESS_TYPES.BLANK}
                                                    href={"/user-groups/add-user-group"}
                                                >
                                                    <Link
                                                        name="Add User Group"
                                                        href={"/user-groups/add-user-group"}
                                                        icon={<PlusIcon className="block h-4 w-4 mr-2"/>}
                                                        extraStyling={"ml-6"}
                                                    />
                                                </AccessControl>
                                            </CollapsibleLinks>
                                        </AccessControl>
                                        <AccessControl
                                            requiredPermission={PERMISSIONS.VIEW_REPORTS}
                                            noAccessType={NO_ACCESS_TYPES.BLANK}
                                        >
                                            <CollapsibleLinks childLinkCount={1} show>
                                                <Link
                                                    name="Reports"
                                                    href={"/reports"}
                                                    icon={<DocumentReportIcon className="block h-4 w-4 mr-2"/>}
                                                    parent
                                                />
                                                <AccessControl
                                                    requiredPermission={PERMISSIONS.UPLOAD_REPORTS}
                                                    noAccessType={NO_ACCESS_TYPES.BLANK}
                                                    href={"/reports/upload-report"}
                                                >
                                                    <Link
                                                        name="Upload Report"
                                                        href={"/reports/upload-report"}
                                                        icon={<UploadIcon className="block h-4 w-4 mr-2"/>}
                                                        extraStyling={"ml-6"}
                                                    />
                                                </AccessControl>
                                            </CollapsibleLinks>
                                        </AccessControl>

                                        {process.env.REACT_APP_FEATURE_ENABLEMENT_LEVEL <= 0 &&
                                            <Link
                                                name="Billing"
                                                href={"/billing"}
                                                icon={<CreditCardIcon className="block h-4 w-4 mr-2"/>}
                                            />
                                        }

                                    </div>
                                    <div className="grid grid-flow-row content-end rounded-lg mt-12">

                                        {process.env.REACT_APP_FEATURE_ENABLEMENT_LEVEL <= 0 &&
                                            <Link
                                                name="Notifications"
                                                href={"/notifications"}
                                                icon={<BellIcon className="block h-4 w-4 mr-2"/>}
                                            />
                                        }

                                        <AccessControl
                                            requiredPermission={PERMISSIONS.ACCESS_HELP_AND_SUPPORT}
                                            noAccessType={NO_ACCESS_TYPES.BLANK}
                                            href={"/help-and-support"}
                                        >
                                            <Link
                                                name="Help & support"
                                                href={"/help-and-support"}
                                                icon={<SupportIcon className="block h-4 w-4 mr-2"/>}
                                            />
                                        </AccessControl>
                                        <Link
                                            name="Settings"
                                            href={"/settings"}
                                            icon={<CogIcon className="block h-4 w-4 mr-2"/>}
                                        />
                                        <div className="p-6 flex items-center">
                                            <Button
                                                colour={BUTTON_VARIANT_TYPES.UNCOLOURED}
                                                text="Logout"
                                                icon={<LogoutIcon className="block h-4 w-4 ml-2"/>}
                                                extraStyling={
                                                    "w-full justify-center bg-blue-gray-200 hover:bg-blue-gray-300 text-blue-gray-600"
                                                }
                                                onClick={handleLogout}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Menu.Item>
                        </Menu.Items>
                    </Transition>
                </>
            )}

        </Menu>
    );
}

export default MobileMenuPopPanel;
